import "./GradeMaster.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import BackIcon from "../../assets/icons/arrow.png";
import editIcon from "../../assets/icons/edit.png";
import { useAuthStore } from "../../Services/authStore";
import { toastSuccess, toastError } from "../../Services/toaster";

// ─────────────────────────────────────────────────────────
//  GRADE MASTER COMPONENT
// ─────────────────────────────────────────────────────────
export default function GradeMaster() {
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    description: "",
    active: true,
  });
  const [formError, setFormError] = useState("");
  const [formLoading, setFormLoading] = useState(false);

  const token = useAuthStore((state) => state.token);
  const API_URL = process.env.REACT_APP_API_URL;

  // ─── GET /garde/list/all ───────────────────────────────────
  const fetchGrades = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/garde/list/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });

      // Handle response - map API fields to component fields
      const data = response.data?.data || response.data || [];
      const mappedGrades = data.map((item) => ({
        id: item.id || item.garde_id,
        description: item.description,
        active: item.is_active === true || item.is_active === 1,
      }));

      setGrades(mappedGrades);
    } catch (err) {
      console.error("Failed to load grades:", err.response?.data);
      toastError("Failed to load grades", "error");
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchGrades();
  }, [fetchGrades]);

  // ─── GET /garde/{garde_id} ───────────────────────────────────
  const fetchGradeById = useCallback(
    async (id) => {
      try {
        const response = await axios.get(`${API_URL}/garde/${id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
          },
        });

        const data = response.data?.data || response.data;
        return {
          id: data.id || data.garde_id,
          description: data.description,
          // active: data.is_active === true || data.is_active === 1,
        };
      } catch (err) {
        console.error("Failed to fetch grade:", err.response?.data);
        throw err;
      }
    },
    [API_URL, token],
  );

  // ─── POST /garde/add ───────────────────────────────────
  const createGrade = async (gradeData) => {
    try {
      const response = await axios.post(
        `${API_URL}/garde/add`,
        {
          description: gradeData.description,
          is_active: gradeData.active,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Create error:", err.response?.data);
      throw err;
    }
  };

  // ─── PUT /garde/update/{garde_id} ────────────────────────────
  const updateGrade = async (id, gradeData) => {
    try {
      const response = await axios.put(
        `${API_URL}/garde/update/${id}`,
        {
          description: gradeData.description,
          is_active: gradeData.active,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            accept: "application/json",
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (err) {
      console.error("Update error:", err.response?.data);
      throw err;
    }
  };

  const filteredGrades = grades.filter((grade) =>
    grade.description?.toLowerCase().includes(searchTerm.trim().toLowerCase()),
  );

  // ─── Handlers ──
  const handleAdd = () => {
    setIsEditMode(false);
    setEditingId(null);
    setFormData({
      description: "",
      active: true,
    });
    setFormError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setIsEditMode(false);
    setEditingId(null);
    setFormData({
      description: "",
      active: true,
    });
    setFormError("");
  };

  const handleEdit = async (grade) => {
    try {
      const gradeData = await fetchGradeById(grade.id);

      setIsEditMode(true);
      setEditingId(grade.id);
      setFormData({
        description: gradeData.description,
        active: gradeData.active,
      });
      setFormError("");
      setShowForm(true);
    } catch (err) {
      toastError("Failed to load grade details", "error");
    }
  };

  const handleToggleActive = async (grade) => {
    const newActive = !grade.active;

    try {
      await updateGrade(grade.id, {
        description: grade.description,
        active: newActive,
      });

      // Update local state
      setGrades((prevGrades) =>
        prevGrades.map((g) =>
          g.id === grade.id ? { ...g, active: newActive } : g,
        ),
      );

      toastSuccess(
        `Grade ${newActive ? "activated" : "deactivated"} successfully`,
        "success",
      );
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to update grade status.";
      toastError(msg, "error");
    }
  };

  const handleSubmit = async () => {
    if (!formData.description.trim()) {
      toastError("Description is required", "error");
      return;
    }

    setFormLoading(true);
    setFormError("");

    try {
      if (isEditMode) {
        // Update existing grade
        await updateGrade(editingId, formData);

        // Update local state
        setGrades((prevGrades) =>
          prevGrades.map((g) =>
            g.id === editingId
              ? {
                  ...g,
                  description: formData.description,
                  active: formData.active,
                }
              : g,
          ),
        );

        toastSuccess("Grade updated successfully", "success");
        setShowForm(false);
        setIsEditMode(false);
        setEditingId(null);
        setFormData({
          description: "",
          active: true,
        });
      } else {
        // Create new grade
        await createGrade(formData);

        // Refresh the list
        await fetchGrades();

        toastSuccess("Grade added successfully", "success");
        setShowForm(false);
        setFormData({
          description: "",
          active: true,
        });
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => `${d.loc?.join(".")} — ${d.msg}`).join("\n")
        : detail || "Failed to save grade.";
      setFormError(msg);
      toastError(msg, "error");
    } finally {
      setFormLoading(false);
    }
  };

  // ─── Form View ───
  if (showForm) {
    return (
      <div className="add-user-page">
        <div className="add-user-header">
          <button className="rm-back-btn" onClick={closeForm}>
            <img src={BackIcon} alt="Back" className="gm-back-icon" />
          </button>
          <div className="header-title">
            <h2>{isEditMode ? "Edit Grade" : "Add Grade"}</h2>
          </div>
        </div>

        <div className="user-form-card">
          {formLoading && (
            <p style={{ color: "#9F8E87", fontSize: 13, marginBottom: 14 }}>
              Saving...
            </p>
          )}
          {formError && (
            <p
              style={{
                color: "#b94040",
                fontSize: 13,
                marginBottom: 14,
                whiteSpace: "pre-line",
              }}
            >
              {formError}
            </p>
          )}

          <div className="form-group">
            <label>Description *</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  description: e.target.value,
                })
              }
              placeholder="Grade Description"
            />
          </div>

          {isEditMode && (
            <div className="form-group">
              <label>Status</label>
              <div className="status-toggle">
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        active: e.target.checked,
                      })
                    }
                  />
                  <span className="slider"></span>
                </label>
                <span>{formData.active ? "Active" : "Inactive"}</span>
              </div>
            </div>
          )}

          <div className="form-footer">
            <button className="btn-secondary" onClick={closeForm}>
              Cancel
            </button>
            <button
              className="save-btn"
              onClick={handleSubmit}
              disabled={formLoading}
            >
              {formLoading ? "Saving..." : isEditMode ? "Update" : "Save"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── List View ───
  return (
    <div className="gm-page">
      {/* ─── Top Bar ─── */}
      <div className="gm-top">
        <div>
          <h1 className="gm-page-title">Grade Master</h1>
          <p className="gm-page-sub">
            Manage member grades and classifications
          </p>
        </div>
        <div className="gm-top-right">
          <div className="gm-search-box">
            <span className="gm-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search grades..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="gm-btn gm-btn-primary" onClick={handleAdd}>
            + Add Grade
          </button>
        </div>
      </div>

      {/* ─── Table ─── */}
      <div className="gm-table-wrap">
        <table className="gm-table">
          <thead>
            <tr>
              <th className="gm-col-id">#</th>
              <th className="gm-col-description">Description</th>
              <th className="gm-col-active">Status</th>
              <th className="gm-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td
                  colSpan="4"
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#9F8E87",
                  }}
                >
                  Loading grades...
                </td>
              </tr>
            ) : filteredGrades.length === 0 ? (
              <tr>
                <td colSpan="4">
                  <div className="gm-empty-state">
                    <div className="gm-empty-icon">📋</div>
                    <div className="gm-empty-title">No Grades Found</div>
                    <div className="gm-empty-desc">
                      {searchTerm
                        ? `No grades match "${searchTerm}"`
                        : "Start by adding your first grade"}
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              filteredGrades.map((grade, index) => (
                <tr key={grade.id}>
                  <td className="gm-col-id">{index + 1}</td>
                  <td className="gm-col-description">{grade.description}</td>
                  <td className="gm-col-active">
                    <label className="gm-switch">
                      <input
                        type="checkbox"
                        checked={grade.active}
                        onChange={() => handleToggleActive(grade)}
                      />
                      <span className="gm-slider"></span>
                    </label>
                  </td>

                  <td className="gm-col-actions">
                    <div className="gm-action-group">
                      <button
                        className="gm-action-btn gm-action-btn-edit"
                        onClick={() => handleEdit(grade)}
                        title="Edit"
                      >
                        <img
                          src={editIcon}
                          alt="Edit"
                          className="gm-action-icon"
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ─── Modal ─── */}
      {showModal && (
        <div className="gm-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="gm-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="gm-modal-title">
              {isEditMode ? "Edit Grade" : "Add New Grade"}
            </h2>
            <p className="gm-modal-sub">
              {isEditMode
                ? "Update the grade description and status"
                : "Create a new member grade classification"}
            </p>

            <div className="gm-form-group">
              <label>Description *</label>
              <input
                type="text"
                placeholder="Enter grade description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              />
            </div>

            <div className="gm-form-group">
              <div className="gm-checkbox-wrap">
                <input
                  type="checkbox"
                  id="gm-active"
                  checked={formData.active}
                  onChange={(e) =>
                    setFormData({ ...formData, active: e.target.checked })
                  }
                />
                <label htmlFor="gm-active">Active</label>
              </div>
            </div>

            <div className="gm-modal-actions">
              <button
                className="gm-btn gm-btn-secondary"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button className="gm-btn gm-btn-primary" onClick={handleSubmit}>
                {isEditMode ? "Update" : "Add"} Grade
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
